import { useCookie } from 'app/hooks/useCookie';
import { cookieConsentDefaultValue } from '@utils/Cookies';
import React, { useMemo, useState } from 'react';
import { Variant } from './CookieConsent';
import { CookieConsentType } from './CookieConsentType';

export type CookieConsentData = {
    heading: string;
    required?: boolean;
    key: CookieConsentType;
    details: Array<string>;
};

export type CookieContextValue = {
    [key in CookieConsentType]?: boolean;
};

export type CookieConsentContextProps = {
    values: CookieContextValue;
    setValues: (values: CookieContextValue) => void;
    isOptedIn: (key: CookieConsentType) => boolean;
    cookieExists?: boolean;
    showConsentModal: { visible: boolean; expanded: boolean };
    setShowConsentModal: (options: { visible: boolean; expanded: boolean }) => void;
    variant: Variant;
    setVariant: (variant: Variant) => void;
};

export const CookieConsentContext = React.createContext<CookieConsentContextProps>({
    values: {
        required_storage: true,
        functionality_storage: true,
        statistics_storage: true,
        ad_storage: true,
    },
    setValues: () => {},
    isOptedIn: () => true,
    cookieExists: false,
    setShowConsentModal: () => {},
    showConsentModal: { visible: false, expanded: false },
    variant: Variant.Original,
    setVariant: () => {},
});

export const CookieConsentContextProvider: React.FC<{ version: number }> = ({
    version,
    children,
}): React.ReactElement => {
    const [cookieValue, setCookieValue, cookieExists] = useCookie(
        `cookie-consent-${version}`,
        cookieConsentDefaultValue(false),
        365
    );

    const [showConsentModal, setShowConsentModal] = useState<{ visible: boolean; expanded: boolean }>({
        visible: false,
        expanded: false,
    });

    const [variant, setVariant] = useState<Variant>(Variant.Original);

    const setValues = (values: Partial<CookieContextValue>) => {
        setCookieValue({ ...cookieValue, ...values });
    };

    const isOptedIn = (key) => cookieValue[key] === true || variant === Variant.Original;

    const value = useMemo(
        () => ({
            values: cookieValue,
            setValues,
            cookieExists,
            isOptedIn,
            showConsentModal,
            setShowConsentModal,
            variant,
            setVariant,
        }),
        [cookieValue, showConsentModal, cookieExists, variant]
    );

    return <CookieConsentContext.Provider value={value}>{children}</CookieConsentContext.Provider>;
};
